import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { authApi } from '../../api/endpoints/auth';
import { ApiError } from '../../api/errors';
import { buildPasswordSchema } from '../../lib/passwordRules';
import { usePasswordPolicy } from '../../hooks/usePasswordPolicy';
import { PasswordRulesChecklist } from '../auth/PasswordRulesChecklist';
import { Button, Input } from '@/kit';

interface FormValues {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const REGION = import.meta.env.VITE_COGNITO_REGION ?? 'us-east-1';

/** Traduce el error del backend a algo accionable para el usuario. */
function friendlyError(error: unknown): string {
    if (error instanceof ApiError) {
        switch (error.code) {
            case 'COGNITO_NOT_AUTHORIZED':
            case 'INVALID_CREDENTIALS':
                return 'La contraseña actual es incorrecta.';
            case 'COGNITO_INVALID_PASSWORD':
                return 'La nueva contraseña no cumple los requisitos de seguridad.';
            case 'COGNITO_RATE_LIMITED':
                return 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.';
        }
    }
    return 'No se pudo cambiar la contraseña. Verifica tu contraseña actual y los requisitos.';
}

export function ChangePasswordForm({ onDone }: { onDone?: () => void }) {
    const { policy } = usePasswordPolicy();
    // Schema dinámico desde la policy del backend (DEC-RBAC-052), con fallback. Memo por policy.
    const schema = useMemo(
        () =>
            z
                .object({
                    oldPassword: z.string().min(1, 'Ingresa tu contraseña actual'),
                    newPassword: buildPasswordSchema(policy),
                    confirmPassword: z.string(),
                })
                .refine((d) => d.newPassword === d.confirmPassword, {
                    message: 'Las contraseñas no coinciden',
                    path: ['confirmPassword'],
                }),
        [policy],
    );
    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
    });
    const mut = useMutation({
        mutationFn: (v: FormValues) =>
            authApi.changePassword({
                region: REGION,
                previousPassword: v.oldPassword,
                proposedPassword: v.newPassword,
            }),
        onSuccess: () => {
            toast.success('Contraseña actualizada');
            form.reset();
            onDone?.();
        },
        onError: (error) => {
            form.setError('root', { message: friendlyError(error) });
        },
    });
    const values = useWatch({ control: form.control });
    const newPassword = values.newPassword ?? '';
    const allFilled = Boolean(values.oldPassword && values.newPassword && values.confirmPassword);
    const rootError = form.formState.errors.root?.message;

    return (
        <form
            onSubmit={(e) => {
                form.clearErrors('root');
                void form.handleSubmit((v) => {
                    mut.mutate(v);
                })(e);
            }}
            className="space-y-4"
        >
            <Input
                id="cp-old"
                type="password"
                label="Contraseña actual"
                autoComplete="current-password"
                {...form.register('oldPassword')}
            />
            <div className="space-y-2">
                <Input
                    id="cp-new"
                    type="password"
                    label="Nueva contraseña"
                    autoComplete="new-password"
                    {...form.register('newPassword')}
                />
                <PasswordRulesChecklist value={newPassword} policy={policy} />
            </div>
            <Input
                id="cp-confirm"
                type="password"
                label="Confirmar nueva contraseña"
                autoComplete="new-password"
                error={form.formState.errors.confirmPassword?.message}
                {...form.register('confirmPassword')}
            />
            {rootError && <p className="text-sm text-destructive">{rootError}</p>}
            {allFilled && (
                <Button type="submit" disabled={mut.isPending}>
                    Guardar
                </Button>
            )}
        </form>
    );
}
